import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Clock, Eye, ShieldCheck, Activity, Landmark } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { getPublicArticles } from "@/lib/articles";
import { CotSnapshot } from "@/components/site/CotSnapshot";
import { MacroToolsStrip } from "@/components/site/MacroToolsStrip";

const SITE = "https://xauproacademy.com";

// Surface anchor: "Gold Market Analysis" category cluster.
// Hybrid structure per the Growth Initiative Review:
// evergreen frame at the top, recurring published reads beneath.
// Recurring layer is sourced from the existing article corpus
// (Macro Analysis + Strategy + Advanced Liquidity) so this surface
// stays in sync with the publication pipeline; no new content type.
const ANALYSIS_CATEGORIES = new Set([
  "Macro Analysis",
  "Strategy",
  "Advanced Liquidity",
  "SMC Foundations",
  "Market Structure",
]);

const byNewest = (a: { publishedAt: string }, b: { publishedAt: string }) =>
  a.publishedAt < b.publishedAt ? 1 : a.publishedAt > b.publishedAt ? -1 : 0;

export const Route = createFileRoute("/gold-analysis")({
  head: () => {
    const reads = getPublicArticles()
      .filter((a) => ANALYSIS_CATEGORIES.has(a.category))
      .sort(byNewest)
      .slice(0, 20);
    return {
      meta: [
        { title: "Gold Market Structure Studies: XAUUSD | XAU Pro Academy" },
        {
          name: "description",
          content:
            "Structural studies of XAUUSD gold: liquidity, sessions, premium and discount, order blocks and macro context, written from an institutional desk perspective.",
        },
        {
          name: "keywords",
          content:
            "gold market analysis, gold analysis today, XAUUSD analysis, gold outlook, gold trade ideas, institutional gold analysis, SMC gold analysis",
        },
        { property: "og:title", content: "How Institutional Desks Read Gold Structure" },
        {
          property: "og:description",
          content:
            "Governed XAUUSD reads: macro layer, liquidity behaviour and SMC structure. Neutral bias is a valid conclusion.",
        },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `${SITE}/gold-analysis` },
      ],
      links: [{ rel: "canonical", href: `${SITE}/gold-analysis` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Gold Market Analysis",
            url: `${SITE}/gold-analysis`,
            description:
              "Institutional XAUUSD market analysis. Macro context, liquidity behaviour and Smart Money structure, published as governed reads.",
            isPartOf: { "@type": "WebSite", name: "XAU Pro Academy", url: SITE },
            hasPart: reads.map((a) => ({
              "@type": "Article",
              headline: a.title,
              description: a.metaDescription,
              datePublished: a.publishedAt,
              dateModified: a.updatedAt,
              url: `${SITE}/education/${a.slug}`,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Gold Market Analysis", item: `${SITE}/gold-analysis` },
            ],
          }),
        },
      ],
    };
  },
  component: GoldAnalysisPage,
});

function GoldAnalysisPage() {
  const reads = getPublicArticles()
    .filter((a) => ANALYSIS_CATEGORIES.has(a.category))
    .sort(byNewest);
  const [latest, ...rest] = reads;

  return (
    <Layout>
      {/* Evergreen frame */}
      <section className="relative overflow-hidden border-b border-border/60 bg-gradient-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div
          className="pointer-events-none absolute -top-40 left-1/2 h-[32rem] w-[56rem] -translate-x-1/2 rounded-full opacity-40 blur-3xl"
          style={{ background: "radial-gradient(closest-side, hsl(var(--gold) / 0.45), transparent)" }}
        />
        <div className="container relative mx-auto max-w-7xl px-4 pb-20 pt-20 md:pt-28">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center rounded-full border border-gold/30 bg-gold-muted px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
              Gold Market Analysis
            </span>
            <h1 className="mt-6 font-display text-4xl font-bold tracking-tight md:text-6xl">
              Structural studies of the{" "}
              <span className="text-gradient-gold">XAUUSD</span> gold market
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
              In-depth reads on how gold actually moves: liquidity, session behaviour, premium and
              discount pricing, institutional zones and macro context. Written to explain market
              structure, not to forecast tomorrow's price.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button asChild variant="gold" size="lg">
                <a href="#reads">Browse studies</a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/orion">How Orion analyses gold</Link>
              </Button>
            </div>
          </div>

          {/* Method strip */}
          <div className="mt-14 grid gap-4 md:grid-cols-3">
            {[
              {
                icon: Landmark,
                label: "Macro layer",
                body: "Real yields, USD, policy posture and event risk are evaluated before structure. Macro instability reduces conviction, not headlines.",
              },
              {
                icon: Activity,
                label: "Structure and liquidity",
                body: "HTF bias from H4 and H1, liquidity sweeps before entry confirmation, displacement before bias change. M15 executes, not decides.",
              },
              {
                icon: ShieldCheck,
                label: "Selectivity",
                body: "Low-quality conditions are rejected. Neutral bias is an output. Frequency is never optimised at the expense of capital.",
              },
            ].map((m) => (
              <div
                key={m.label}
                className="rounded-xl border border-border/60 bg-card/60 p-5 backdrop-blur"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gold-muted text-gold">
                  <m.icon className="h-4 w-4" />
                </div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                  {m.label}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CotSnapshot />
      <MacroToolsStrip />

      {/* Recurring reads */}
      <section id="reads" className="container mx-auto max-w-7xl px-4 py-14">
        <div className="flex items-end justify-between border-b border-border/60 pb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Newest first
            </div>
            <h2 className="mt-1 font-display text-2xl font-bold md:text-3xl">
              XAUUSD structure studies
            </h2>
          </div>
          <Link
            to="/education"
            className="hidden text-sm font-semibold text-gold hover:underline md:inline"
          >
            Full education hub <ArrowRight className="inline h-3.5 w-3.5" />
          </Link>
        </div>

        {latest && (
          <Link
            to="/education/$slug"
            params={{ slug: latest.slug }}
            className="group mt-8 block overflow-hidden rounded-2xl border border-border/60 bg-card transition hover:border-gold/40"
          >
            <div className="grid md:grid-cols-2">
              <div
                className={`relative aspect-video overflow-hidden bg-gradient-to-br ${latest.coverGradient} md:aspect-auto`}
              >
                {latest.heroImage ? (
                  <img
                    src={latest.heroImage}
                    alt={latest.title}
                    width={1536}
                    height={864}
                    loading="eager"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 grid-bg opacity-40" />
                )}
              </div>
              <div className="p-8 md:p-10">
                <div className="flex items-center gap-3 text-xs">
                  <span className="rounded-full bg-gold-muted px-3 py-1 font-semibold uppercase tracking-wider text-gold">
                    Newest study
                  </span>
                  <span className="text-muted-foreground">{latest.category}</span>
                </div>
                <h3 className="mt-4 font-display text-2xl font-bold leading-tight md:text-3xl">
                  {latest.title}
                </h3>
                <p className="mt-3 text-muted-foreground">{latest.excerpt}</p>
                <div className="mt-6 flex items-center justify-between border-t border-border/60 pt-5 text-xs text-muted-foreground">
                  <span>
                    {new Date(latest.publishedAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-gold">
                    Read analysis{" "}
                    <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                  </span>
                </div>
              </div>
            </div>
          </Link>
        )}

        {rest.length > 0 && (
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((a) => (
              <Link
                key={a.slug}
                to="/education/$slug"
                params={{ slug: a.slug }}
                className="group flex flex-col rounded-xl border border-border/60 bg-card p-6 transition hover:border-gold/40"
              >
                <div className="text-xs font-semibold uppercase tracking-wider text-gold">
                  {a.category}
                </div>
                <h3 className="mt-3 font-display text-lg font-bold leading-snug">{a.title}</h3>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{a.excerpt}</p>
                <div className="mt-5 flex items-center justify-between border-t border-border/60 pt-4 text-xs text-muted-foreground">
                  <span>
                    {new Date(a.publishedAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {a.readTime}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Trust Chain handoff */}
      <section className="border-t border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-7xl px-4 py-16">
          <div className="mx-auto max-w-3xl text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
              From analysis to decision
            </div>
            <h2 className="mt-3 font-display text-3xl font-bold md:text-4xl">
              Analysis is the entry point. The Trust Chain is the destination.
            </h2>
            <p className="mt-3 text-muted-foreground">
              Every published read connects to the same operational surfaces that traders
              evaluate before becoming members.
            </p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                to: "/signals" as const,
                icon: Activity,
                label: "Published Decisions",
                body: "The live decision stream, team and Orion, with full reasoning.",
              },
              {
                to: "/validation" as const,
                icon: ShieldCheck,
                label: "Validation Hub",
                body: "How Orion is validated against historical XAUUSD data, in public.",
              },
              {
                to: "/track-record" as const,
                icon: Eye,
                label: "Track Record",
                body: "Per-decision audit of outcomes, with no curation or hiding.",
              },
              {
                to: "/orion" as const,
                icon: Landmark,
                label: "Orion Engine",
                body: "The SMC and ICT decision engine behind the published reads.",
              },
            ].map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="group flex flex-col rounded-xl border border-border/60 bg-card p-6 transition hover:border-gold/40"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold-muted text-gold">
                  <c.icon className="h-5 w-5" />
                </div>
                <div className="mt-4 font-display text-lg font-bold">{c.label}</div>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{c.body}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-gold">
                  Open{" "}
                  <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-3">
            <Button asChild variant="gold" size="lg">
              <Link to="/pricing">Membership options</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/orion">How Orion decides</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
